import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import { parse } from 'date-fns';
import * as Yup from 'yup';

import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Refresh, Save } from '@material-ui/icons';
import { isArray } from 'lodash';

import { DateTime } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import { validation } from '~/helpers/yupValidations';
import api from '~/services/api';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  toolbar: {
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'minmax(180px, 0.9fr) 280px minmax(260px, 1.4fr) auto',
    marginBottom: theme.spacing(1.5),
  },
  toolbarButtons: {
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap',
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    boxShadow: 'none',
  },
  inputCell: {
    width: 260,
  },
  inputField: {
    width: 150,
  },
  inputWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
  },
  calculatedValue: {
    minWidth: 72,
    whiteSpace: 'nowrap',
  },
  totalRow: {
    '& td': {
      fontSize: '1.5rem',
      fontWeight: 700,
    },
  },
  totalCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5),
  },
}));

const formatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function label(key, fallback) {
  const translated = t(key);
  return translated === key ? fallback : translated;
}

function formatNumber(value) {
  return formatter.format(Number(value || 0));
}

function toInteger(value) {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? Math.trunc(numberValue) : 0;
}

function getNovoEstoqueItem(item, valores) {
  const valor = Number(valores[item.idpostotipoareia] || 0);

  if (isSilo(item)) {
    return valor;
  }

  return valor * Number(item.pesounitariokgtipoareia || 0);
}

function isSilo(item) {
  return String(item.silotipoareia || '').toUpperCase() === 'S';
}

function getOrigemAreia(item) {
  if (isSilo(item)) {
    return label('s.operacao.transferenciaareiasilo.silo', 'Silo');
  }

  return label('s.operacao.transferenciaareiasilo.saco', 'Areia em saco');
}

function getQuantidadeSacos(item) {
  if (isSilo(item)) {
    return '';
  }

  const pesoUnitario = Number(item.pesounitariokgtipoareia || 0);

  if (pesoUnitario <= 0) {
    return 0;
  }

  return Number(item.estoquepostotipoareia || 0) / pesoUnitario;
}

function getItemApresentacao(item) {
  const quantidadeSacos = getQuantidadeSacos(item);

  return {
    ...item,
    tipoareiaapuracao: item.descricaotipoareia || '',
    origemareiaapuracao: getOrigemAreia(item),
    quantidadesacosapuracao:
      quantidadeSacos === '' ? '' : formatNumber(quantidadeSacos),
    estoqueatualapuracao: formatNumber(item.estoquepostotipoareia),
  };
}

function pad(value) {
  return String(value).padStart(2, '0');
}

function toDateTimeValue(date) {
  return `${pad(date.getDate())}/${pad(
    date.getMonth() + 1
  )}/${date.getFullYear()} ${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}:${pad(date.getSeconds())}`;
}

function toApiDateTime(value) {
  if (!value) return null;

  if (value.includes('/')) return value;

  const [date, time] = value.split('T');
  const [year, month, day] = date.split('-');
  const normalizedTime = time && time.length === 5 ? `${time}:00` : time;
  return `${day}/${month}/${year} ${normalizedTime}`;
}

function sanitizeObservacao(value) {
  return value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ0-9\s]/g, '');
}

export default function ApuracaoEstoqueAreia() {
  const formRef = useRef(null);
  const classes = useStyles();
  const [postos, setPostos] = useState([]);
  const [idposto, setIdposto] = useState('');
  const [datahora, setDatahora] = useState(toDateTimeValue(new Date()));
  const [observacao, setObservacao] = useState('');
  const [observacaoError, setObservacaoError] = useState('');
  const [valores, setValores] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const postoSelecionado = useMemo(
    () => postos.find((posto) => String(posto.idposto) === String(idposto)),
    [postos, idposto]
  );

  const itens = postoSelecionado?.itens || [];

  const itensApresentacao = useMemo(
    () => itens.map((item) => getItemApresentacao(item)),
    [itens]
  );

  const totalEstoqueAtual = useMemo(
    () =>
      itens.reduce(
        (total, item) => total + Number(item.estoquepostotipoareia || 0),
        0
      ),
    [itens]
  );

  const totalNovoEstoque = useMemo(
    () =>
      itens.reduce(
        (total, item) => total + getNovoEstoqueItem(item, valores),
        0
      ),
    [itens, valores]
  );

  const handleLoad = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get('estoqueareia/resumo');
      const lista = Array.isArray(data) ? data : [];
      setPostos(lista);
    } catch (erros) {
      if (isArray(erros)) erros.map((e) => toast.error(t(e)));
      else toast.error(t(erros));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  useEffect(() => {
    const proximosValores = {};
    itens.forEach((item) => {
      const saldo = Number(item.estoquepostotipoareia || 0);
      const peso = Number(item.pesounitariokgtipoareia || 0);

      proximosValores[item.idpostotipoareia] = isSilo(item)
        ? toInteger(saldo)
        : peso > 0
        ? toInteger(saldo / peso)
        : 0;
    });
    setValores(proximosValores);
  }, [itens]);

  const handleChangeValor = useCallback((item, value) => {
    const integerValue = String(value).replace(/\D/g, '');

    setValores((state) => ({
      ...state,
      [item.idpostotipoareia]: integerValue,
    }));
  }, []);

  const validateFields = useCallback(async () => {
    const formData = formRef.current ? formRef.current.getData() : {};
    const datahoraApuracao = formData.datahoraapuracaoestoqueareia || datahora;

    try {
      formRef.current?.setErrors({});
      setObservacaoError('');

      const schema = Yup.object().shape({
        datahoraapuracaoestoqueareia: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'datahoraapuracaoestoqueareia',
            label(
              's.operacao.apuracaoestoqueareia.datahorainvalida',
              'Data/Hora invalida.'
            ),
            (value) => value instanceof Date && !Number.isNaN(value.getTime())
          ),
        observacaoapuracaoestoqueareia: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(255, t('s.geral.validacao.tamanhomaximo').replace('%s', 255))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });

      await validation(schema, {
        datahoraapuracaoestoqueareia: datahoraApuracao,
        observacaoapuracaoestoqueareia: observacao,
      });

      return datahoraApuracao;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = {};

        error.inner.forEach((e) => {
          if (e.path === 'observacaoapuracaoestoqueareia') {
            setObservacaoError(e.message);
          } else {
            errors[e.path] = e.message;
          }
        });

        formRef.current?.setErrors(errors);
      } else {
        toast.error(t(error));
      }

      return null;
    }
  }, [datahora, observacao]);

  const handleChangeObservacao = useCallback((event) => {
    setObservacao(sanitizeObservacao(event.target.value).slice(0, 255));
    setObservacaoError('');
  }, []);

  const handleSave = useCallback(async () => {
    if (!postoSelecionado) {
      toast.error(label('c.menu.stock.posto', 'Posto'));
      return;
    }

    const siloAcimaCapacidade = itens.find((item) => {
      if (!isSilo(item) || item.capacidadepostotipoareia == null) {
        return false;
      }

      return (
        Number(valores[item.idpostotipoareia] || 0) >
        Number(item.capacidadepostotipoareia)
      );
    });

    if (siloAcimaCapacidade) {
      toast.error(
        `O estoque do silo ${
          siloAcimaCapacidade.nomepostotipoareia ||
          siloAcimaCapacidade.descricaotipoareia
        } nao pode ultrapassar a capacidade de ${formatNumber(
          siloAcimaCapacidade.capacidadepostotipoareia
        )}.`
      );
      return;
    }

    const datahoraApuracao = await validateFields();

    if (!datahoraApuracao) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        idposto: postoSelecionado.idposto,
        datahoraapuracaoestoqueareia: toApiDateTime(datahoraApuracao),
        observacaoapuracaoestoqueareia: observacao || null,
        itens: itens.map((item) => {
          const valor = toInteger(valores[item.idpostotipoareia]);

          return {
            idpostotipoareia: item.idpostotipoareia,
            quantidadeapuracaoestoqueareiaitem: isSilo(item) ? 0 : valor,
            volumeapuracaoestoqueareiaitem: isSilo(item) ? valor : 0,
          };
        }),
      };

      await api.post('apuracaoestoqueareia', payload);
      toast.success(t('s.geral.sucessoinclusao'));
      await handleLoad();
    } catch (erros) {
      if (isArray(erros)) erros.map((e) => toast.error(t(e)));
      else toast.error(t(erros));
    } finally {
      setSaving(false);
    }
  }, [
    handleLoad,
    itens,
    observacao,
    postoSelecionado,
    validateFields,
    valores,
  ]);

  return (
    <Form
      ref={formRef}
      initialData={{ datahoraapuracaoestoqueareia: datahora }}
      className={classes.root}
    >
      <div className={classes.toolbar}>
        <FormControl variant="outlined" size="small">
          <InputLabel>{label('s.menu.configuracao.posto', 'Posto')}</InputLabel>
          <Select
            label={label('s.menu.configuracao.posto', 'Posto')}
            value={idposto}
            onChange={(event) => setIdposto(event.target.value)}
          >
            <MenuItem value="">
              <em>{label('s.geral.selecione', 'Selecione')}</em>
            </MenuItem>
            {postos.map((posto) => (
              <MenuItem key={posto.idposto} value={posto.idposto}>
                {posto.codigonomeposto}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DateTime
          time
          name="datahoraapuracaoestoqueareia"
          label={label(
            's.operacao.transferenciaareiasilo.datahora',
            'Data/Hora'
          )}
          getValue={setDatahora}
        />

        <TextField
          label={label('s.geral.observacao', 'Observacao')}
          value={observacao}
          onChange={handleChangeObservacao}
          error={!!observacaoError}
          helperText={observacaoError}
          variant="outlined"
          size="small"
          inputProps={{ maxLength: 255 }}
        />

        <div className={classes.toolbarButtons}>
          <Button
            color="primary"
            disabled={loading || saving}
            onClick={handleLoad}
            startIcon={<Refresh />}
            variant="outlined"
          >
            {label('s.geral.atualizar', 'Atualizar')}
          </Button>

          <Button
            color="primary"
            disabled={loading || saving || itens.length === 0}
            onClick={handleSave}
            startIcon={<Save />}
            variant="contained"
          >
            {label('s.geral.salvar', 'Salvar')}
          </Button>
        </div>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        {(loading || saving) && <LinearProgress variant="query" />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {label('s.cadastro.pitstop.tipoareia', 'Tipo de areia')}
              </TableCell>
              <TableCell>
                {label('s.operacao.estoqueareia.origem', 'Origem')}
              </TableCell>
              <TableCell>Quantidade de sacos</TableCell>
              <TableCell align="right">
                {label('s.operacao.apuracaoestoqueareia.estoqueatual')}
              </TableCell>
              <TableCell align="left" className={classes.inputCell}>
                {label('s.operacao.apuracaoestoqueareia.novoestoque')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {itensApresentacao.map((item) => (
              <TableRow key={item.idpostotipoareia}>
                <TableCell>{item.tipoareiaapuracao}</TableCell>
                <TableCell>{item.origemareiaapuracao}</TableCell>
                <TableCell>{item.quantidadesacosapuracao}</TableCell>
                <TableCell align="right" className={classes.totalCell}>
                  {item.estoqueatualapuracao}
                </TableCell>
                <TableCell align="left" className={classes.inputCell}>
                  <div className={classes.inputWrapper}>
                    <TextField
                      className={classes.inputField}
                      type="number"
                      value={valores[item.idpostotipoareia] ?? ''}
                      onChange={(event) =>
                        handleChangeValor(item, event.target.value)
                      }
                      variant="outlined"
                      size="small"
                      InputProps={{
                        endAdornment: isSilo(item) ? null : (
                          <InputAdornment position="end">Saco</InputAdornment>
                        ),
                      }}
                      inputProps={{ inputMode: 'numeric', min: 0, step: 1 }}
                    />
                    {isSilo(item) ? null : (
                      <Typography
                        className={classes.calculatedValue}
                        variant="body2"
                      >
                        {`${formatNumber(
                          getNovoEstoqueItem(item, valores)
                        )} kg`}
                      </Typography>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {itens.length > 0 && (
            <TableFooter>
              <TableRow className={classes.totalRow}>
                <TableCell>{label('s.geral.total', 'Total')}</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" className={classes.totalCell}>
                  {formatNumber(totalEstoqueAtual)}
                </TableCell>
                <TableCell align="left" className={classes.totalCell}>
                  {formatNumber(totalNovoEstoque)}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
          {!loading && itens.length === 0 && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography className={classes.empty} variant="body2">
                    {label(
                      's.operacao.estoqueareia.semestoque',
                      'Sem estoque de areia cadastrado.'
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Form>
  );
}
